import React, { useEffect, useRef, useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import AdminLayout from "../../layout/AdminLayout";
import {
  addEntertainmentVideo,
  getEntertainmentVideoById,
  updateEntertainmentVideo,
} from "../../../api/entertainmentVideo.api"; // Update with your API methods
import { errorMsg, success } from "../../../utils/notifications";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";

const initialData = {
  name: "",
  video: "",
  category: "",
  startDate: "",
  endDate: "",
  duration: "",
  location: "",
  status: "",
};

const types = ["vehicle", "restaurant"]; // Updated from categories
const locations = [
  { value: "Paris, France", label: "Paris, France" },
  { value: "Douala, Cameroon", label: "Douala, Cameroon" },
];
function AddEntertainmentVideo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [videoData, setVideoData] = useState(initialData);
  const [selectedFile, setSelectedFile] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (id) {
      getEntertainmentVideoById(id)
        .then((res) => {
          const videoDetails = res?.data;

          const formattedStartDate = videoDetails.startDate
            ? new Date(videoDetails.startDate).toISOString().split("T")[0]
            : "";
          const formattedEndDate = videoDetails.endDate
            ? new Date(videoDetails.endDate).toISOString().split("T")[0]
            : "";

          setVideoData({
            ...videoDetails,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            type: {
              value: videoDetails.type,
              label: videoDetails.type,
            },
            location: {
              value: videoDetails.location,
              label: videoDetails.location,
            },
            video: videoDetails.video, // Ensure video is set
          });
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setVideoData({
      ...videoData,
      [name]: newValue,
    });
  };
  const validateFile = (file) => {
    const maxFileSize = 100 * 1024 * 1024; // 100MB in bytes

    return new Promise((resolve, reject) => {
      if (file.size > maxFileSize) {
        return reject("Selected video exceeds 100MB file size limit.");
      }

      const video = document.createElement("video");
      video.onloadedmetadata = () => {
        const duration = video.duration;
        if (duration > 21) {
          return reject("Selected video exceeds 20 seconds duration.");
        }

        const width = video.videoWidth;
        const height = video.videoHeight;
        const maxWidth = 3840;
        const maxHeight = 2160;

        if (width >= maxWidth || height >= maxHeight) {
          return reject(
            `Video dimensions exceed maximum allowed: ${maxWidth}x${maxHeight}.`
          );
        }

        const isPortrait = height > width;
        const isLandscape = width > height;

        if (videoData.type?.value === "restaurant" && !isPortrait) {
          return reject("Please upload a portrait video.");
        }

        if (videoData.type?.value === "vehicle" && !isLandscape) {
          return reject("Please upload a landscape video.");
        }

        resolve();
      };

      video.src = URL.createObjectURL(file);
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    validateFile(file)
      .then(() => setSelectedFile(file))
      .catch((err) => {
        setSelectedFile(null);
        errorMsg("Error", err);
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile && !videoData.video) {
      errorMsg(
        "Missing Video",
        "Please upload a valid video before submitting."
      );
      setLoading(false);
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("name", videoData.name);
    formData.append("video", selectedFile);
    formData.append("type", videoData.type.value); // Changed from category
    formData.append("category", videoData.category);
    formData.append("startDate", videoData.startDate);
    formData.append("endDate", videoData.endDate);
    formData.append("duration", videoData.duration);
    formData.append("location", videoData.location.value);

    try {
      if (id) {
        await updateEntertainmentVideo(id, formData);
        success("Successful", "Entertainment Video Updated Successfully");
        navigate(`/infotainmentvideos/${id}`);
      } else {
        await addEntertainmentVideo(formData);
        success("Successful", "Entertainment Video Added Successfully");
        navigate("/infotainmentvideos");
      }
      setVideoData(initialData);
      setSelectedFile(null);
    } catch (err) {
      errorMsg("Oops", err);
    } finally {
      setLoading(false);
    }
  };

  const resetVideo = () => {
    setSelectedFile(null);
    setVideoData({ ...videoData, video: "" });
    inputRef.current.value = "";
  };

  return (
    <AdminLayout
      title={id ? "Update Infotainment  Video" : "Add Infotainment  Video"}
    >
      <form onSubmit={handleSubmit} method="POST">
        <div className="gap-5 px-3 md:px-5 lg:px-5 py-0.5 md:py-1.5 lg:py-1.5 grid grid-cols-1 lg:grid-cols-12">
          {/* Left side for other input fields */}
          <div className="lg:col-span-8 md:col-span-12 col-span-12 px-1 lg:px-5 md:px-5">
            <div className="gap-4 py-0.5 md:py-3 lg:py-3 grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
              {/* Name Input */}
              <div className="lg:col-span-6 md:col-span-6 col-span-12">
                <label
                  htmlFor="name"
                  className="block text-base font-semibold leading-6 text-gray-600"
                >
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter Name"
                  value={videoData.name}
                  onChange={handleChange}
                  id="name"
                  className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellow sm:leading-6 focus:ring-transparent"
                  required
                />
              </div>

              {/* Type Select Dropdown */}
              <div className="lg:col-span-6 md:col-span-6 col-span-12">
                <label
                  htmlFor="type"
                  className="block text-base font-semibold leading-6 text-gray-600"
                >
                  Type
                </label>
                <Select
                  name="type"
                  id="type"
                  options={types.map((type) => ({
                    value: type,
                    label: type.charAt(0).toUpperCase() + type.slice(1),
                  }))}
                  value={videoData.type}
                  className="basic-multi-select"
                  classNamePrefix="select Type"
                  onChange={(selectedOption) =>
                    setVideoData({ ...videoData, type: selectedOption })
                  }
                  required
                  isDisabled={!!id}
                />
              </div>

              {/* Start Date Input */}
              <div className="lg:col-span-6 md:col-span-6 col-span-12 mt-4">
                <label
                  htmlFor="startDate"
                  className="block text-base font-semibold leading-6 text-gray-600"
                >
                  Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={videoData.startDate}
                  onChange={handleChange}
                  className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellow sm:leading-6 focus:ring-transparent"
                  required
                  min={new Date().toISOString().split("T")[0]} // Prevent past dates
                />
              </div>

              {/* End Date Input */}
              <div className="lg:col-span-6 md:col-span-6 col-span-12 mt-4">
                <label
                  htmlFor="endDate"
                  className="block text-base font-semibold leading-6 text-gray-600"
                >
                  End Date
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={videoData.endDate}
                  onChange={handleChange}
                  className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellow sm:leading-6 focus:ring-transparent"
                  required
                />
              </div>

              {/* Location Select */}
              <div className="lg:col-span-6 md:col-span-6 col-span-12 ">
                <label
                  htmlFor="location"
                  className="block text-base font-semibold leading-6 mb-1 text-gray-600"
                >
                  Location
                </label>
                <Select
                  name="location"
                  id="location"
                  options={locations}
                  value={videoData.location}
                  className="basic-multi-select"
                  classNamePrefix="select Location"
                  onChange={(selectedOption) =>
                    setVideoData({ ...videoData, location: selectedOption })
                  }
                  required
                />
              </div>
              <div className="lg:col-span-6  md:col-span-6 col-span-12">
                <label
                  htmlFor="category"
                  className="block text-base font-semibold mb-1 leading-6 text-gray-600"
                >
                  Category
                </label>
                <Select
                  name="category"
                  id="category"
                  options={[
                    { value: "Entertainment", label: "Entertainment" },
                    { value: "Information", label: "Information" },
                  ]}
                  value={{
                    value: videoData?.category,
                    label: videoData?.category,
                  }}
                  className="basic-multi-select"
                  classNamePrefix="select category"
                  onChange={(selectedOption) =>
                    setVideoData({
                      ...videoData,
                      category: selectedOption?.value,
                    })
                  }
                  required
                  isDisabled={!!id}
                />
              </div>
            </div>
          </div>

          {/* Right side for Add Video */}
          <div className="lg:col-span-4 md:col-span-12 col-span-12 px-1 lg:px-5 md:px-5">
            <div className="flex flex-col items-end">
              <div className="flex items-center justify-between w-full">
                <label
                  htmlFor="video"
                  className="block text-base font-semibold leading-6 text-gray-600"
                >
                  Add Video
                </label>
                <button
                  type="button"
                  className="flex justify-center rounded-md border border-gray-400 py-1 px-4 text-xs leading-6 text-gray-800 shadow-sm hover:bg-opacity-90"
                  onClick={resetVideo}
                >
                  Reset Video
                </button>
              </div>

              <label
                htmlFor="video"
                className="border-2 border-dashed border-gray-300 h-72 w-full mt-2 flex items-center justify-center cursor-pointer"
              >
                {selectedFile ? (
                  <video
                    src={URL.createObjectURL(selectedFile)}
                    className="h-full w-full object-cover"
                    controls
                    autoPlay
                    loop
                    muted
                    onCanPlay={(e) => e.target.play()} // Ensures video starts playing
                  />
                ) : videoData.video ? (
                  <video
                    src={videoData.video}
                    className="h-full w-full object-cover"
                    controls
                    autoPlay
                    loop
                    muted
                    onCanPlay={(e) => e.target.play()} // Ensures video starts playing
                  />
                ) : (
                  <AiOutlinePlus className="text-gray-400 h-14 w-14 p-2" />
                )}
              </label>

              <input
                ref={inputRef}
                type="file"
                name="video"
                id="video"
                className="hidden"
                accept="video/*"
                onChange={handleFileChange}
              />
              {videoData?.type?.value && (
                <p className="text-base text-red-500 mt-2 me-auto">
                  Note: Please upload a{" "}
                  {videoData.type.value === "restaurant"
                    ? "portrait"
                    : videoData.type.value === "vehicle"
                    ? "landscape"
                    : "correct"}{" "}
                  video.
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end items-center mt-4 mr-10">
          <button
            type="submit"
            disabled={loading} // Disable button while loading is true
            className={`flex items-center bg-yellow-500 hover:bg-yellow-600 text-white py-2 px-4 rounded ${
              loading ? "cursor-not-allowed" : ""
            }`}
          >
            {loading ? (
              <span>Loading...</span>
            ) : (
              <>
                <AiOutlinePlus className="mr-2" />{" "}
                {id ? "Update Video" : "Add Video"}
              </>
            )}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

export default AddEntertainmentVideo;
